import { IS_WINDOWS } from 'src/helpers/environment';
import type { CSSObject } from 'tss-react';
import { makeStyles } from 'tss-react/mui';

import {
  ALWAYS_VISIBLE_SCROLLBAR,
  CUSTOM_SCROLLBAR_STANDARD_WIDTH,
  CUSTOM_SCROLLBAR_WIDTH,
  SCROLL_BUTTONS,
  SLIM_SCROLLBARS,
} from '../environment';

export const SCROLLBAR_BACKGROUND_COLOR = IS_WINDOWS ? 'rgb(252, 252, 252)' : 'rgb(250, 250, 250)';
export const SCROLLBAR_BORDER_COLOR = IS_WINDOWS ? undefined : 'rgb(232, 232, 232)';
export const SCROLLBAR_HAS_BORDER = !!SCROLLBAR_BORDER_COLOR;

/** The color of the scrollbar thumb while the user is interacting with the scrollbar. */
export const SCROLLBAR_THUMB_ACTIVE_COLOR = IS_WINDOWS ? 'rgb(99, 99, 99)' : 'rgba(0, 0, 0, 0.5)';

/** The default color of the scrollbar thumb. */
export const SCROLLBAR_THUMB_INACTIVE_COLOR = IS_WINDOWS
  ? 'rgba(0, 0, 0, 0.45)'
  : ALWAYS_VISIBLE_SCROLLBAR
    ? 'rgb(194, 194, 194)'
    : 'rgba(0, 0, 0, 0.5)';

export const SCROLLBAR_BORDER_WIDTH = SCROLLBAR_HAS_BORDER ? 1 : 0;

export const SCROLLBAR_BORDER = SCROLLBAR_HAS_BORDER
  ? `${SCROLLBAR_BORDER_WIDTH}px solid ${SCROLLBAR_BORDER_COLOR}`
  : undefined;

export const SCROLLBAR_BORDER_TRANSPARENT = SCROLLBAR_HAS_BORDER
  ? `${SCROLLBAR_BORDER_WIDTH}px solid transparent`
  : undefined;

const ALWAYS_SELECTOR = '&';
const NEVER_SELECTOR = '&:not(*)';

export const useScrollbarStyles = makeStyles<{
  direction: 'horizontal' | 'vertical';
  visibleWhenNotOverflowing: boolean;
}>()((theme, { direction, visibleWhenNotOverflowing }) => {
  const ifStatic = ALWAYS_VISIBLE_SCROLLBAR ? ALWAYS_SELECTOR : NEVER_SELECTOR;
  const ifOverlay = ALWAYS_VISIBLE_SCROLLBAR ? NEVER_SELECTOR : ALWAYS_SELECTOR;

  const ifHovering = selfOrParent('[data-scrollbar-hover]');
  const ifDragging = selfOrParent('[data-scrollbar-dragging]');
  const ifActive = selfOrParent('[data-scrollbar-recently-active]');
  const unlessOverflowing = selfOrParent('[data-scrollable-overflowing="false"]');

  const ifInteracting = `${ifHovering}, ${ifDragging}`;
  const ifActiveOrInteracting = `${ifActive}, ${ifInteracting}`;

  const isHorizontal = direction === 'horizontal';
  const thicknessKey = isHorizontal ? 'height' : 'width';
  const insideBorderKey = isHorizontal ? 'borderTop' : 'borderLeft';
  const outsideBorderKey = isHorizontal ? 'borderBottom' : 'borderRight';

  return {
    /** The buttons that appear on some platforms at the start and end of the scrollbar. */
    scrollButton: {
      '&:active': {
        color: SCROLLBAR_THUMB_ACTIVE_COLOR,
        /** Shrink the icon slightly while pressed, matching Windows. */
        svg: { transform: 'scale(0.90)' },
      },
      alignSelf: 'center',
      color: SCROLLBAR_THUMB_INACTIVE_COLOR,
      cursor: 'default',
      /** Only show the button if enabled. */
      display: SCROLL_BUTTONS ? undefined : 'none',
      /**
       * For overlay scrollbars, the button should only be visible while interacting with the
       * scrollbar.
       */
      [ifOverlay]: {
        [ifInteracting]: { opacity: 1 },
        opacity: 0,
        transition: 'opacity ease 250ms',
      },
      padding: 3,
      svg: { height: '100%', width: '100%' },
      [thicknessKey]: '100%',
      /** Always hide the button if the scrollable is not overflowing. */
      [unlessOverflowing]: { display: 'none' },
    },

    /**
     * The top-level wrapper for the scrollbar, containing the scroll buttons (if present) and the
     * inner scrollbar element.
     */
    scrollbar: {
      display: 'flex',
      flexDirection: isHorizontal ? 'row' : 'column',
      [ifOverlay]: {
        bottom: 0,
        /** Overlay scrollbars only respond to pointer events while visible. */
        [ifActiveOrInteracting]: { pointerEvents: 'auto' },
        [ifInteracting]: {
          /** Overlay scrollbars gain a visible background while interacting with them. */
          background: SCROLLBAR_BACKGROUND_COLOR,
          [insideBorderKey]: SCROLLBAR_BORDER,
          [outsideBorderKey]: SCROLLBAR_BORDER,
          /** Slim scrollbars become full-width while interacting with them. */
          [thicknessKey]: CUSTOM_SCROLLBAR_STANDARD_WIDTH,
          /**
           * If multiple overlay scrollbars overlap, the one the user is interacting with should be
           * on top.
           */
          zIndex: theme.appZIndex.scrollbar + 1,
        },
        [insideBorderKey]: SCROLLBAR_BORDER_TRANSPARENT,
        [outsideBorderKey]: SCROLLBAR_BORDER_TRANSPARENT,
        pointerEvents: 'none',
        position: 'absolute',
        right: 0,
        /**
         * Slim overlay scrollbars should include room for the borders to ensure they are wide
         * enough.
         */
        [thicknessKey]: CUSTOM_SCROLLBAR_WIDTH + (SLIM_SCROLLBARS ? SCROLLBAR_BORDER_WIDTH * 2 : 0),
        transition: `${thicknessKey} ease 250ms, background ease 250ms, border ease 250ms`,
        [unlessOverflowing]: { display: 'none' },
        [isHorizontal ? 'left' : 'top']: 0,
        /** Overlay scrollbars should not be covered by any other element. */
        zIndex: theme.appZIndex.scrollbar,
      },
      [ifStatic]: {
        background: SCROLLBAR_BACKGROUND_COLOR,
        /**
         * Static scrollbars should appear on top of all other elements while interacting with them.
         */
        [ifInteracting]: { zIndex: theme.appZIndex.scrollbar },
        [insideBorderKey]: SCROLLBAR_BORDER,
        [outsideBorderKey]: SCROLLBAR_BORDER,
        /**
         * The parent of the scrollable is a CSS grid with only one explicit grid cell (the
         * scrollable). If a static scrollbar is visible, it extends the grid by occupying the
         * second row or column (read about the "implicit grid" on MDN for more info).
         */
        [isHorizontal ? 'gridRow' : 'gridColumn']: 2,
        [thicknessKey]: CUSTOM_SCROLLBAR_WIDTH,
        /**
         * Unless visibleWhenNotOverflowing is true, hide the scrollbar if the scrollable is not
         * overflowing.
         */
        [unlessOverflowing]: !visibleWhenNotOverflowing && { display: 'none' },
      },
      /** Prevent touch devices from scrolling while the user is trying to scroll. */
      touchAction: 'none',
    },

    /** The scrollbar track. For accessibility purposes, this is the true scrollbar. */
    scrollbarInner: {
      height: '100%',
      /**
       * Ensure correct spacing at the start and end of the scrollbar track regardless of whether
       * the scroll buttons are visible. */
      margin: SCROLL_BUTTONS ? 0 : isHorizontal ? '0 3px' : '3px 0',
      width: '100%',
    },

    /**
     * The thumb that moves along the scrollbar track as the user scrolls, and which receives
     * pointer events to allow the user to drag it. Unlike the visible part of the thumb, this
     * element occupies the full width of the scrollbar track to make it easier to click.
     */
    scrollbarThumb: {
      padding: isHorizontal ? '3px 0' : '0 3px',
      [thicknessKey]: '100%',
    },

    /** The visible part of the scrollbar thumb. */
    scrollbarThumbVisible: {
      background: SCROLLBAR_THUMB_INACTIVE_COLOR,
      borderRadius: 'calc(infinity * 1px)',
      height: '100%',
      [ifInteracting]: { background: SCROLLBAR_THUMB_ACTIVE_COLOR },
      /**
       * The thumb of an overlay scrollbar should only be visible while scrolling or when
       * interacting with the scrollbar.
       */
      [ifOverlay]: {
        [ifActiveOrInteracting]: { opacity: 1 },
        opacity: 0,
        transition: 'opacity ease 250ms',
      },
      /** Always hide the thumb if the scrollable is not overflowing. */
      [unlessOverflowing]: { display: 'none' },
      width: '100%',
    },
  } satisfies CSSObject;
});

/**
 * Augment a simple CSS selector to match the current element if it or an ancestor matches the
 * selector.
 */
function selfOrParent(selector: string) {
  return `${selector} &, &${selector}`;
}
